import React, { useState, useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import classNames from 'classnames';

import {
  TeacherGetAttendancesQuery,
  useCreateAttendanceMutation,
  useDeleteAttendancesMutation,
  useGetAttendanceTypesQuery,
} from '@school_ify/schoolify-repository/src/teacher/types';

import { calculateNodeOffset } from 'clean/utils/functions';
import Paper from 'clean/components/common/Paper/Paper';
import EntryTitle from 'clean/components/common/EntryTitle/EntryTitle';
import TextField from 'clean/components/common/TextField/TextField';
import Button from 'clean/components/common/Button/Button';

import './Details.scoped.scss';
import i18n from 'translations/i18n';
import DropDown from 'clean/components/common/DropDown/DropDown';
import { ReactComponent as AbsentIcon } from 'assets/icons/attendance_absent.svg';
import { ReactComponent as LateIcon } from 'assets/icons/attendance_late.svg';
import { ReactComponent as ExemptIcon } from 'assets/icons/attendance_exempt.svg';
import { ReactComponent as RemarkIcon } from 'assets/icons/attendance_remark.svg';

interface Props {
  date: string;
  studentId: string;
  attendance?: TeacherGetAttendancesQuery['attendances'][0];
  rootNode: React.RefObject<HTMLElement>;
  onClose: () => void;
  classTitle: string;
  subjectTitle: string;
  attendanceItems?: TeacherGetAttendancesQuery['attendances'];
  classId: string;
  semesterId: string;
  subjectId: string;
  refetchAttendances: () => void;
}

const Details: React.FC<Props> = ({
  rootNode,
  studentId,
  attendance,
  onClose,
  classTitle,
  subjectTitle,
  date,
  attendanceItems,
  classId,
  semesterId,
  subjectId,
  refetchAttendances,
}) => {
  const { data: attendanceTypes } = useGetAttendanceTypesQuery();
  const topOffset = 90;
  const detailsNode = useRef(null);
  const { t } = useTranslation();

  const [offset, setOffset] = useState({ x: null, y: null });

  const [type, setType] = useState(attendance?.type?.value || null);
  const [comment, setComment] = useState(attendance?.comment || '');

  const updateOffset = () => {
    if (detailsNode.current) {
      const nodeOffsets = calculateNodeOffset(rootNode.current);
      let top = nodeOffsets.y - topOffset;
      let left = nodeOffsets.x + rootNode.current.offsetWidth - 1; // -1 is so the border doesn't glitch out when running non-native resolution

      // Means that there is a collision on right side, and we should move it back to the left side
      if (window.innerWidth - left < detailsNode.current.offsetWidth) {
        left -= detailsNode.current.offsetWidth + rootNode.current.offsetWidth - 1; // The same reason
      }

      if (window.innerHeight - top < detailsNode.current.offsetHeight) {
        top -= detailsNode.current.offsetHeight - (window.innerHeight - top) + 10;
      }

      setOffset({ x: left, y: top });
    }
  };

  const [createAttendance] = useCreateAttendanceMutation();
  const [deleteAttendance] = useDeleteAttendancesMutation();

  const save = async () => {
    const idsToDelete = attendanceItems.map(item => item.id);
    if (idsToDelete.length > 0) {
      await deleteAttendance({
        variables: {
          where: {
            id: { _in: idsToDelete },
          },
        },
      });
    }

    if (type || comment) {
      await createAttendance({
        variables: {
          attendances: [
            {
              attendance_type: type === '' ? null : type,
              class_id: classId,
              comment,
              date,
              semester_id: semesterId,
              student_id: studentId,
              subject_id: subjectId,
            },
          ],
        },
      });
    }

    refetchAttendances();
    onClose();
  };

  useEffect(updateOffset, [detailsNode.current]);

  const getIcon = (type, styles) => {
    return {
      absent: <AbsentIcon style={styles} />,
      late: <LateIcon style={styles} />,
      exempt: <ExemptIcon style={styles} />,
      remark: <RemarkIcon style={styles} />,
    }[type];
  };

  const selectedTypeIcon = (attendanceTypes?.attendance_types || []).find(i => i.value === type);

  return (
    <div
      ref={detailsNode}
      style={{ '--offset-x': offset.x, '--offset-y': offset.y } as React.CSSProperties}
      className="grade-details-wrapper"
    >
      <div
        onClick={e => {
          e.stopPropagation();
          onClose();
        }}
        className="backdrop-overlay"
      ></div>
      <div className="wrapper">
        <Paper>
          <div className="content">
            <div className="title">{t('attendance:attendance')}</div>

            <div
              className="close"
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
            />

            <div className="entry">
              <EntryTitle title={t('common:Date')}>
                {
                  <TextField
                    disabled
                    value={DateTime.fromFormat(date, 'y-MM-dd')
                      .setLocale(i18n.language)
                      .toFormat('MM.dd.y')}
                    onChange={() => null}
                  />
                }
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('grades:class')}>
                <TextField disabled value={classTitle} onChange={() => null} />
              </EntryTitle>
            </div>
            <div className="entry">
              <EntryTitle title={t('attendance:subject')}>
                <TextField disabled value={subjectTitle} onChange={() => null} />
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('attendance:attendanceType')}>
                <DropDown
                  options={(attendanceTypes?.attendance_types || []).map(item => ({
                    ...item,
                    title: item.text,
                  }))}
                  labelProperty="title"
                  idProperty="value"
                  value={type}
                  iconStart={getIcon(selectedTypeIcon?.icon, { marginLeft: '4px' })}
                  emptyItemLabel={t('attendance:select')}
                  onChange={setType}
                  renderRow={({
                    item: attendanceType,
                    classes: defaultClasses,
                    onClick,
                    isSelected,
                  }) => {
                    return (
                      <div
                        key={attendanceType.value}
                        onClick={() => onClick(attendanceType)}
                        className={classNames(defaultClasses.outer, {
                          [defaultClasses.outerSelected]: isSelected,
                        })}
                      >
                        <div className={defaultClasses.inner}>
                          {attendanceType.icon &&
                            getIcon(attendanceType.icon, { marginRight: '10px' })}
                          {attendanceType.title}
                        </div>
                      </div>
                    );
                  }}
                />
              </EntryTitle>
            </div>

            <div className="entry">
              <EntryTitle title={t('common:Comment')}>
                <TextField
                  multiLine
                  value={comment}
                  onChange={e => {
                    setComment(e.target.value);
                  }}
                />
              </EntryTitle>
            </div>

            <div className="entry button">
              <Button
                onClick={save}
                label={t('common:save')}
                color="alternative-primary"
                variant="filled"
              />
            </div>
          </div>
        </Paper>
      </div>
    </div>
  );
};

export default Details;
